import type { ColDef, ColGroupDef, GridOptions } from '@ag-grid-community/core';
import type {
  FilterBarField,
  SelectedFilterField,
} from '@cdw-ae/filter-bar-interfaces';
import type {
  GaugeSummaryConfig,
  ReportData,
} from '../report-viewer/report-viewer.model';

/**
 * Abstract base class for report handlers
 * Each report type should extend this class and implement the required methods
 */
export abstract class BaseReportHandler {
  protected reportData: ReportData;

  constructor(reportData: ReportData) {
    this.reportData = reportData;
  }

  /**
   * Get column definitions for the report grid
   */
  abstract getColumnDefs(): (ColDef | ColGroupDef)[];

  /**
   * Get grid options for the report
   */
  abstract getGridOptions(): GridOptions;

  /**
   * Get filter bar settings for the report
   * Returns null if the report doesn't support filtering
   */
  static getFilterBarSettings(): FilterBarField[] | null {
    return null;
  }

  /**
   * Get initial filters that should be applied by default
   * Returns null if no default filters
   */
  static getInitialFilters(): SelectedFilterField[] | null {
    return null;
  }

  /**
   * Create a default 'Status = ACTIVE' filter from the given filter settings.
   * Shared across report handlers that use StateAnalysisReportFilterSettings.
   */
  static getDefaultActiveStatusFilter(
    filterSettings: FilterBarField[]
  ): SelectedFilterField[] | null {
    const statusField = filterSettings.find(f => f.name === 'status');
    if (!statusField) {
      // biome-ignore lint/suspicious/noConsole: intentional warning for missing filter configuration
      console.warn('[BaseReportHandler] Status field not found in filter settings');
      return null;
    }
    return [{
      matchValue: JSON.stringify(['ACTIVE']),
      field: statusField,
      selectedMatcher: statusField.fieldMatchers[0],
      uid: 'status-active-default',
    }];
  }

  /**
   * Process raw API data with filters and return data ready for the grid
   * Each report handler implements its own filtering and aggregation logic
   * @param rawData Raw data from the API
   * @param filters Filters to apply to the data
   * @returns Processed data ready for display in the grid
   */
  static processRawData(
    rawData: unknown[],
    _filters: SelectedFilterField[]
  ): unknown[] {
    // Default implementation returns raw data unmodified
    return rawData;
  }

  /**
   * Get the raw data for the report
   */
  getData(): unknown {
    return this.reportData.data;
  }

  /**
   * Optional lifecycle hook called before grid setup
   * Override this to perform initialization that requires access to the data
   * @param _data The aggregate data for the report
   */
  initializeWithData(_data: unknown[]): void {
    // Default implementation does nothing
    // Subclasses can override to perform data-dependent initialization
  }

  /**
   * Get gauge summary configuration for reports that display gauge cards
   * Returns null for reports that don't use gauge summaries
   * @param _filteredData Optional filtered data to calculate summary from (if not provided, uses all data)
   * @returns Gauge summary configuration or null
   */
  getGaugeSummaryConfig(_filteredData?: unknown[]): GaugeSummaryConfig | null {
    // Default implementation returns null
    // Reports with gauge summaries override this method
    return null;
  }
}
